import {
  Building2,
  PackageSearch,
  Activity,
  BellRing,
  ShieldAlert,
  CalendarCheck,
  ShieldCheck,
  Languages,
  UtensilsCrossed,
  ScanBarcode,
  CreditCard,
  ChefHat,
  History,
  QrCode,
  Settings,
  Bot,
  LineChart,
  BarChart2,
  FileCode,
  Database,
  Package,
  Send,
  GitBranch,
  Zap,
  Gauge,
  Wallet,
  Users,
  Compass,
  Palmtree,
  Scissors,
  Sparkles,
  Search,
  Tag,
  Camera,
  ClipboardCheck,
  CalendarDays,
  ArrowDownToLine,
  Droplets,
  Stethoscope,
  Lock,
  Star,
  ScanFace,
  WifiOff,
  Clock,
  Cpu,
  ScrollText,
  MessageCircle,
  CalendarRange,
} from 'lucide-react';
import type { ProjectConfig } from './types';

const inventory: ProjectConfig = {
  slug: 'inventory',
  ns: 'portfolio',
  navKey: 'nav.portfolio_inventory',
  cardTitleKey: 'portfolioIndex.inventory.title',
  cardDescKey: 'portfolioIndex.inventory.desc',
  domainKey: 'portfolioIndex.inventory.domain',
  thumbnail: '/inventory/images/dashboard.png',
  featured: true,
  order: 1,
  imagePrefix: '/inventory/images/',
  orbs: [
    'orb absolute top-40 left-1/4 w-96 h-96 bg-neon-cyan/10 rounded-full blur-3xl',
    'orb-delayed absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-neon-purple/10 rounded-full blur-3xl opacity-50',
  ],
  hero: {
    accent: 'green',
    pillAccent: 'cyan',
    badgeGlow: false,
    titleGradient: 'gradient-text',
    tech: ['Next.js', 'React Query', 'TailwindCSS', 'NestJS', 'Prisma', 'PostgreSQL', 'Docker'],
    cta: {
      variant: 'credentials',
      href: 'https://inventory.happybuild.cloud',
      buttonKey: 'demo.button',
      credentialsTitleKey: 'demo.credentialsTitle',
      noteKey: 'demo.note',
      roles: [
        { labelKey: 'demo.adminRole', accent: 'cyan', email: 'admin@demo.com', password: 'Demo@123456' },
        { labelKey: 'demo.managerRole', accent: 'purple', email: 'manager@demo.com', password: 'Demo@123456' },
      ],
    },
  },
  bento: {
    iconBorder: false,
    features: [
      { icon: Building2, size: 'wide', accent: 'cyan', titleKey: 'features.multiTenant.title', descKey: 'features.multiTenant.desc', orbPos: 'tr' },
      { icon: BellRing, size: 'small', accent: 'purple', titleKey: 'features.smartAlerts.title', descKey: 'features.smartAlerts.desc' },
      { icon: PackageSearch, size: 'wide', accent: 'green', titleKey: 'features.productManagement.title', descKey: 'features.productManagement.desc', orbPos: 'br', descBottomGap: true },
      { icon: Activity, size: 'small', accent: 'cyan', titleKey: 'features.stockMovements.title', descKey: 'features.stockMovements.desc' },
      { icon: ShieldAlert, size: 'full', accent: 'cyan', titleKey: 'features.security.title', descKey: 'features.security.desc' },
    ],
  },
  showcaseHeadingKey: 'modules.title',
  showcase: [
    {
      kind: 'modules',
      modules: [
        { key: 'dashboard', images: [{ src: 'dashboard.png', labelKey: 'dashboard' }, { src: 'notifications.png', labelKey: 'notifications' }] },
        { key: 'operations', images: [{ src: 'inventory.png', labelKey: 'inventory' }, { src: 'receipts.png', labelKey: 'receipts' }, { src: 'issues.png', labelKey: 'issues' }, { src: 'adjustments.png', labelKey: 'adjustments' }] },
        { key: 'catalog', images: [{ src: 'products.png', labelKey: 'products' }, { src: 'categories.png', labelKey: 'categories' }, { src: 'warehouses.png', labelKey: 'warehouses' }] },
        { key: 'security', images: [{ src: 'audit-logs.png', labelKey: 'audit' }, { src: 'users.png', labelKey: 'users' }] },
        { key: 'system', images: [{ src: 'integrations.png', labelKey: 'integrations' }, { src: 'reports.png', labelKey: 'reports' }, { src: 'settings.png', labelKey: 'settings' }] },
      ],
    },
  ],
};

const petclinic: ProjectConfig = {
  slug: 'pet-clinic',
  ns: 'petclinic',
  navKey: 'nav.portfolio_pet_clinic',
  cardTitleKey: 'portfolioIndex.petClinic.title',
  cardDescKey: 'portfolioIndex.petClinic.desc',
  domainKey: 'portfolioIndex.petClinic.domain',
  thumbnail: '/pet-clinic/thumbnail.png',
  featured: true,
  order: 4,
  imagePrefix: '/pet-clinic/',
  orbs: [
    'orb absolute top-40 left-1/4 w-96 h-96 bg-neon-cyan/10 rounded-full blur-3xl',
    'orb-delayed absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-neon-blue/10 rounded-full blur-3xl opacity-50',
    'absolute top-1/4 right-1/3 w-80 h-80 bg-neon-green/5 rounded-full blur-3xl',
  ],
  hero: {
    accent: 'cyan',
    pillAccent: 'blue',
    badgeGlow: true,
    titleGradient: 'from-neon-cyan via-neon-blue to-neon-green',
    tech: ['Next.js', 'TypeScript', 'Prisma', 'PostgreSQL', 'TailwindCSS', 'shadcn/ui'],
  },
  bento: {
    iconBorder: true,
    features: [
      { icon: CalendarCheck, size: 'wide', accent: 'cyan', titleKey: 'features.queue.title', descKey: 'features.queue.desc', orbPos: 'tr' },
      { icon: Stethoscope, size: 'small', accent: 'blue', titleKey: 'features.records.title', descKey: 'features.records.desc' },
      { icon: FileCode, size: 'small', accent: 'green', titleKey: 'features.rx.title', descKey: 'features.rx.desc' },
      { icon: CreditCard, size: 'wide', accent: 'purple', titleKey: 'features.billing.title', descKey: 'features.billing.desc', orbPos: 'bl', descBottomGap: true },
      { icon: Package, size: 'small', accent: 'cyan', titleKey: 'features.inventory.title', descKey: 'features.inventory.desc' },
      { icon: ShieldCheck, size: 'full', accent: 'green', titleKey: 'features.compliance.title', descKey: 'features.compliance.desc' },
    ],
  },
  showcaseHeadingKey: 'screensHeading',
  showcase: [
    {
      kind: 'modules',
      modules: [
        { key: 'daily', images: [{ src: 'clinic-queue.png', labelKey: 'queue' }, { src: 'clinic-appointments.png', labelKey: 'appointments' }] },
        { key: 'medical', images: [{ src: 'clinic-patient.png', labelKey: 'patient' }, { src: 'clinic-exam.png', labelKey: 'exam' }, { src: 'clinic-prescription.png', labelKey: 'prescription' }] },
        { key: 'pharmacy', images: [{ src: 'clinic-inventory.png', labelKey: 'inventory' }, { src: 'clinic-controlled.png', labelKey: 'controlled' }, { src: 'clinic-vaccine-cert.png', labelKey: 'vaccineCert' }] },
        { key: 'billing', images: [{ src: 'clinic-pos.png', labelKey: 'pos' }, { src: 'clinic-receipts.png', labelKey: 'receipts' }, { src: 'clinic-reports.png', labelKey: 'reports' }] },
        { key: 'portal', images: [{ src: 'owner-login.png', labelKey: 'ownerLogin' }, { src: 'owner-portal.png', labelKey: 'ownerPortal' }] },
      ],
    },
  ],
};

const pos: ProjectConfig = {
  slug: 'pos',
  ns: 'pos',
  navKey: 'nav.portfolio_pos',
  cardTitleKey: 'portfolioIndex.pos.title',
  cardDescKey: 'portfolioIndex.pos.desc',
  domainKey: 'portfolioIndex.pos.domain',
  thumbnail: '/pos/dashboard_page_1774637185050.png',
  featured: true,
  order: 3,
  imagePrefix: '/pos/',
  orbs: [
    'orb absolute top-40 left-1/4 w-96 h-96 bg-neon-purple/10 rounded-full blur-3xl',
    'orb-delayed absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-neon-cyan/10 rounded-full blur-3xl opacity-50',
  ],
  hero: {
    accent: 'purple',
    pillAccent: 'purple',
    badgeGlow: true,
    titleGradient: 'from-neon-purple via-neon-cyan to-neon-purple',
    tech: ['Next.js 16', 'TypeScript', 'TailwindCSS', 'Recharts', 'Offline-First', 'JSON Storage'],
    cta: { variant: 'outline', href: 'https://pos.happybuild.cloud', buttonKey: 'tryDemo' },
  },
  bento: {
    iconBorder: true,
    features: [
      { icon: ScanBarcode, size: 'wide', accent: 'cyan', titleKey: 'features.pos.title', descKey: 'features.pos.desc', orbPos: 'tr' },
      { icon: CreditCard, size: 'small', accent: 'purple', titleKey: 'features.checkout.title', descKey: 'features.checkout.desc' },
      { icon: ChefHat, size: 'small', accent: 'orange', titleKey: 'features.kds.title', descKey: 'features.kds.desc' },
      { icon: History, size: 'wide', accent: 'green', titleKey: 'features.openOrders.title', descKey: 'features.openOrders.desc', orbPos: 'bl', descBottomGap: true },
      { icon: QrCode, size: 'small', accent: 'cyan', titleKey: 'features.qrMenu.title', descKey: 'features.qrMenu.desc' },
      { icon: Settings, size: 'full', accent: 'purple', titleKey: 'features.dashboard.title', descKey: 'features.dashboard.desc' },
    ],
  },
  showcaseHeadingKey: 'uiConcepts',
  showcase: [
    { kind: 'row', reverse: true, titleKey: 'concepts.pos.title', descKey: 'concepts.pos.desc', image: { src: 'pos_page_with_items_1774637421424.png', altKey: 'concepts.pos.alt' }, imgFit: 'cover', gradient: 'cyan-purple' },
    {
      kind: 'pair',
      columns: [
        { titleKey: 'concepts.kds.title', descKey: 'concepts.kds.desc', images: [{ src: 'kds_page_1774637466748.png', altKey: 'concepts.kds.alt' }], imgFit: 'contain', gradient: 'orange-cyan' },
        { titleKey: 'concepts.openOrders.title', descKey: 'concepts.openOrders.desc', images: [{ src: 'open_orders_page_1774637485283.png', altKey: 'concepts.openOrders.alt' }], imgFit: 'contain', gradient: 'green-cyan' },
      ],
    },
    {
      kind: 'collage',
      headings: [
        { titleKey: 'concepts.dashboard.title', descKey: 'concepts.dashboard.desc' },
        { titleKey: 'concepts.settings.title', descKey: 'concepts.settings.desc' },
        { titleKey: 'concepts.qrMenu.title', descKey: 'concepts.qrMenu.desc' },
      ],
      hero: { src: 'dashboard_page_1774637185050.png', altKey: 'concepts.dashboard.alt' },
      rows: [[{ src: 'menu_qr_page_1774637547329.png', altKey: 'concepts.qrMenu.alt' }, { src: 'settings_page_1774637508252.png', altKey: 'concepts.settings.alt' }]],
      gradient: 'purple-cyan',
      gap: 'gap-6',
    },
  ],
};

const tradeplanner: ProjectConfig = {
  slug: 'tradeplanner',
  ns: 'tradeplanner',
  navKey: 'nav.portfolio_tradeplanner',
  cardTitleKey: 'portfolioIndex.tradeplanner.title',
  cardDescKey: 'portfolioIndex.tradeplanner.desc',
  domainKey: 'portfolioIndex.tradeplanner.domain',
  thumbnail: '/tradeplanner/images/dashboard.png',
  order: 2,
  imagePrefix: '/tradeplanner/images/',
  orbs: [
    'orb absolute top-40 left-1/4 w-96 h-96 bg-neon-blue/10 rounded-full blur-3xl',
    'orb-delayed absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-neon-green/10 rounded-full blur-3xl opacity-50',
  ],
  hero: {
    accent: 'blue',
    pillAccent: 'blue',
    badgeGlow: true,
    badgeGlowGradient: 'from-neon-blue/20 to-neon-green/20',
    titleGradient: 'from-neon-blue via-neon-cyan to-neon-green',
    tech: ['Python', 'React', 'FastAPI', 'Pandas', 'WebSockets', 'TradingView'],
    cta: { variant: 'outline', href: 'https://tradeplanner.happybuild.cloud', buttonKey: 'tryDemo' },
  },
  bento: {
    iconBorder: true,
    features: [
      { icon: Activity, size: 'wide', accent: 'blue', titleKey: 'features.dashboard.title', descKey: 'features.dashboard.desc', orbPos: 'tr' },
      { icon: LineChart, size: 'small', accent: 'green', titleKey: 'features.signals.title', descKey: 'features.signals.desc' },
      { icon: Bot, size: 'small', accent: 'purple', titleKey: 'features.bot.title', descKey: 'features.bot.desc' },
      { icon: BarChart2, size: 'wide', accent: 'cyan', titleKey: 'features.tools.title', descKey: 'features.tools.desc', orbPos: 'bl', descBottomGap: true },
    ],
  },
  showcaseHeadingKey: 'uiConcepts',
  showcase: [
    { kind: 'row', reverse: true, titleKey: 'concepts.dashboard.title', descKey: 'concepts.dashboard.desc', image: { src: 'dashboard.png', altKey: 'concepts.dashboard.alt' }, imgFit: 'cover', gradient: 'blue-green' },
    {
      kind: 'pair',
      columns: [
        { titleKey: 'concepts.signals.title', descKey: 'concepts.signals.desc', images: [{ src: 'signals.png', altKey: 'concepts.signals.alt' }, { src: 'brief.png', altKey: 'concepts.signals.alt' }], imgFit: 'contain', gradient: 'cyan-blue' },
        { titleKey: 'concepts.bot.title', descKey: 'concepts.bot.desc', images: [{ src: 'bot.png', altKey: 'concepts.bot.alt' }], imgFit: 'contain', gradient: 'green-purple' },
      ],
    },
    {
      kind: 'collage',
      headings: [
        { titleKey: 'concepts.tools1.title', descKey: 'concepts.tools1.desc' },
        { titleKey: 'concepts.tools2.title', descKey: 'concepts.tools2.desc' },
        { titleKey: 'concepts.tools3.title', descKey: 'concepts.tools3.desc' },
      ],
      hero: { src: 'paper_trading.png', altKey: 'concepts.tools1.alt' },
      rows: [
        [{ src: 'backtest.png', altKey: 'concepts.tools1.alt' }, { src: 'walkforward.png', altKey: 'concepts.tools1.alt' }],
        [{ src: 'risk.png', altKey: 'concepts.tools2.alt' }, { src: 'dca.png', altKey: 'concepts.tools2.alt' }],
        [{ src: 'portfolio_risk.png', altKey: 'concepts.tools2.alt' }, { src: 'optimize.png', altKey: 'concepts.tools2.alt' }],
      ],
      gradient: 'blue-cyan',
      gap: 'gap-4',
    },
  ],
};

const bangkoktable4: ProjectConfig = {
  slug: 'bangkoktable4',
  ns: 'bangkoktable4',
  navKey: 'nav.portfolio_bangkoktable4',
  cardTitleKey: 'portfolioIndex.bangkoktable4.title',
  cardDescKey: 'portfolioIndex.bangkoktable4.desc',
  domainKey: 'portfolioIndex.bangkoktable4.domain',
  thumbnail: '/bangkoktable4/section_1.png',
  featured: true,
  order: 5,
  imagePrefix: '/bangkoktable4/',
  orbs: [
    'orb absolute top-40 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl',
    'orb-delayed absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-neon-purple/10 rounded-full blur-3xl opacity-50',
  ],
  hero: {
    accent: 'orange',
    pillAccent: 'orange',
    badgeGlow: false,
    titleGradient: 'gradient-text',
    tech: ['TanStack Start', 'React 19', 'Vite', 'Tailwind v4', 'Vercel', 'i18n (FR/EN)'],
    cta: { variant: 'outline', href: 'https://bangkok-table4.vercel.app', buttonKey: 'visitSite' },
  },
  bento: {
    iconBorder: true,
    features: [
      { icon: Languages, size: 'wide', accent: 'orange', titleKey: 'features.design.title', descKey: 'features.design.desc', orbPos: 'tr' },
      { icon: CalendarCheck, size: 'small', accent: 'purple', titleKey: 'features.booking.title', descKey: 'features.booking.desc' },
      { icon: ChefHat, size: 'small', accent: 'cyan', titleKey: 'features.chef.title', descKey: 'features.chef.desc' },
      { icon: UtensilsCrossed, size: 'wide', accent: 'green', titleKey: 'features.catering.title', descKey: 'features.catering.desc', orbPos: 'bl', descBottomGap: true },
    ],
  },
  showcaseHeadingKey: 'showcaseHeading',
  showcase: [
    { kind: 'row', titleKey: 'concepts.hero.title', descKey: 'concepts.hero.desc', image: { src: 'section_1.png', altKey: 'concepts.hero.alt' }, imgFit: 'cover', gradient: 'orange-cyan' },
    { kind: 'row', reverse: true, titleKey: 'concepts.services.title', descKey: 'concepts.services.desc', image: { src: 'section_2.png', altKey: 'concepts.services.alt' }, imgFit: 'cover', gradient: 'cyan-purple' },
    { kind: 'row', titleKey: 'concepts.dishes.title', descKey: 'concepts.dishes.desc', image: { src: 'section_6.png', altKey: 'concepts.dishes.alt' }, imgFit: 'cover', gradient: 'orange-cyan' },
    { kind: 'row', reverse: true, titleKey: 'concepts.venue.title', descKey: 'concepts.venue.desc', image: { src: 'section_4.png', altKey: 'concepts.venue.alt' }, imgFit: 'cover', gradient: 'green-cyan' },
  ],
};

const qaranger: ProjectConfig = {
  slug: 'qaranger',
  ns: 'qaranger',
  navKey: 'nav.portfolio_qaranger',
  cardTitleKey: 'portfolioIndex.qaranger.title',
  cardDescKey: 'portfolioIndex.qaranger.desc',
  domainKey: 'portfolioIndex.qaranger.domain',
  thumbnail: '/qaranger/scenarios.png',
  featured: true,
  order: 6,
  imagePrefix: '/qaranger/',
  orbs: [
    'orb absolute top-40 left-1/4 w-96 h-96 bg-neon-green/10 rounded-full blur-3xl',
    'orb-delayed absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-neon-cyan/10 rounded-full blur-3xl opacity-50',
  ],
  hero: {
    accent: 'green',
    pillAccent: 'green',
    badgeGlow: true,
    badgeGlowGradient: 'from-neon-green/20 to-neon-cyan/20',
    titleGradient: 'from-neon-green via-neon-cyan to-neon-blue',
    tech: ['Go', 'React', 'Vite', 'testcontainers', 'PostgreSQL', 'Kafka'],
  },
  bento: {
    iconBorder: true,
    features: [
      { icon: FileCode, size: 'wide', accent: 'green', titleKey: 'features.scenarios.title', descKey: 'features.scenarios.desc', orbPos: 'tr' },
      { icon: Database, size: 'small', accent: 'cyan', titleKey: 'features.datastores.title', descKey: 'features.datastores.desc' },
      { icon: Package, size: 'small', accent: 'purple', titleKey: 'features.binary.title', descKey: 'features.binary.desc' },
      { icon: Send, size: 'wide', accent: 'cyan', titleKey: 'features.collector.title', descKey: 'features.collector.desc', orbPos: 'bl', descBottomGap: true },
      { icon: GitBranch, size: 'full', accent: 'green', titleKey: 'features.promote.title', descKey: 'features.promote.desc' },
    ],
  },
  showcaseHeadingKey: 'showcaseHeading',
  showcase: [
    { kind: 'row', titleKey: 'concepts.collector.title', descKey: 'concepts.collector.desc', image: { src: 'collector.png', altKey: 'concepts.collector.alt' }, imgFit: 'cover', gradient: 'green-cyan' },
    { kind: 'row', reverse: true, titleKey: 'concepts.import.title', descKey: 'concepts.import.desc', image: { src: 'import.png', altKey: 'concepts.import.alt' }, imgFit: 'cover', gradient: 'cyan-blue' },
    { kind: 'row', titleKey: 'concepts.scenarios.title', descKey: 'concepts.scenarios.desc', image: { src: 'scenarios.png', altKey: 'concepts.scenarios.alt' }, imgFit: 'cover', gradient: 'green-cyan' },
  ],
};

const csenergy: ProjectConfig = {
  slug: 'csenergy',
  ns: 'csenergy',
  navKey: 'nav.portfolio_csenergy',
  cardTitleKey: 'portfolioIndex.csenergy.title',
  cardDescKey: 'portfolioIndex.csenergy.desc',
  domainKey: 'portfolioIndex.csenergy.domain',
  thumbnail: '/csenergy/dashboard.png',
  featured: true,
  order: 7,
  imagePrefix: '/csenergy/',
  orbs: [
    'orb absolute top-40 left-1/4 w-96 h-96 bg-neon-green/10 rounded-full blur-3xl',
    'orb-delayed absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-neon-cyan/10 rounded-full blur-3xl opacity-50',
  ],
  hero: {
    accent: 'green',
    pillAccent: 'green',
    badgeGlow: true,
    badgeGlowGradient: 'from-neon-green/20 to-neon-cyan/20',
    titleGradient: 'from-neon-green via-neon-cyan to-neon-blue',
    tech: ['React', 'TypeScript', 'Vite', 'Tailwind', 'shadcn/ui', 'Recharts', 'TanStack Table', 'OCPP 2.0.1'],
  },
  bento: {
    iconBorder: true,
    features: [
      { icon: Zap, size: 'wide', accent: 'green', titleKey: 'features.control.title', descKey: 'features.control.desc', orbPos: 'tr' },
      { icon: Gauge, size: 'small', accent: 'cyan', titleKey: 'features.dashboard.title', descKey: 'features.dashboard.desc' },
      { icon: Wallet, size: 'small', accent: 'purple', titleKey: 'features.pricing.title', descKey: 'features.pricing.desc' },
      { icon: Activity, size: 'wide', accent: 'cyan', titleKey: 'features.settlement.title', descKey: 'features.settlement.desc', orbPos: 'bl', descBottomGap: true },
      { icon: Users, size: 'full', accent: 'green', titleKey: 'features.multitenant.title', descKey: 'features.multitenant.desc' },
    ],
  },
  showcaseHeadingKey: 'conceptsHeading',
  showcase: [
    { kind: 'video', src: 'demo.mp4', poster: 'dashboard.png' },
    { kind: 'row', titleKey: 'concepts.dashboard.title', descKey: 'concepts.dashboard.desc', image: { src: 'dashboard.png', altKey: 'concepts.dashboard.alt' }, imgFit: 'cover', gradient: 'green-cyan' },
    { kind: 'row', reverse: true, titleKey: 'concepts.chargers.title', descKey: 'concepts.chargers.desc', image: { src: 'chargers.png', altKey: 'concepts.chargers.alt' }, imgFit: 'cover', gradient: 'cyan-purple' },
    { kind: 'row', titleKey: 'concepts.transactions.title', descKey: 'concepts.transactions.desc', image: { src: 'transactions.png', altKey: 'concepts.transactions.alt' }, imgFit: 'cover', gradient: 'green-cyan' },
    { kind: 'row', reverse: true, titleKey: 'concepts.pricing.title', descKey: 'concepts.pricing.desc', image: { src: 'pricing.png', altKey: 'concepts.pricing.alt' }, imgFit: 'cover', gradient: 'cyan-blue' },
    { kind: 'row', titleKey: 'concepts.merchants.title', descKey: 'concepts.merchants.desc', image: { src: 'merchants.png', altKey: 'concepts.merchants.alt' }, imgFit: 'cover', gradient: 'green-cyan' },
  ],
};

const tourandaman: ProjectConfig = {
  slug: 'tourandaman',
  ns: 'tourandaman',
  navKey: 'nav.portfolio_tourandaman',
  cardTitleKey: 'portfolioIndex.tourandaman.title',
  cardDescKey: 'portfolioIndex.tourandaman.desc',
  domainKey: 'portfolioIndex.tourandaman.domain',
  thumbnail: '/tourandaman/home.png',
  featured: true,
  order: 8,
  imagePrefix: '/tourandaman/',
  orbs: [
    'orb absolute top-40 left-1/4 w-96 h-96 bg-neon-blue/10 rounded-full blur-3xl',
    'orb-delayed absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-neon-cyan/10 rounded-full blur-3xl opacity-50',
  ],
  hero: {
    accent: 'blue',
    pillAccent: 'blue',
    badgeGlow: true,
    badgeGlowGradient: 'from-neon-blue/20 to-neon-cyan/20',
    titleGradient: 'from-neon-blue via-neon-cyan to-neon-green',
    tech: ['Next.js', 'React', 'TypeScript', 'Prisma', 'Tailwind', 'shadcn/ui', 'PWA', 'Stripe'],
  },
  bento: {
    iconBorder: true,
    features: [
      { icon: Compass, size: 'wide', accent: 'blue', titleKey: 'features.discover.title', descKey: 'features.discover.desc', orbPos: 'tr' },
      { icon: CalendarCheck, size: 'small', accent: 'cyan', titleKey: 'features.availability.title', descKey: 'features.availability.desc' },
      { icon: CreditCard, size: 'small', accent: 'green', titleKey: 'features.pay.title', descKey: 'features.pay.desc' },
      { icon: Palmtree, size: 'wide', accent: 'cyan', titleKey: 'features.bundles.title', descKey: 'features.bundles.desc', orbPos: 'bl', descBottomGap: true },
      { icon: BellRing, size: 'full', accent: 'blue', titleKey: 'features.confirm.title', descKey: 'features.confirm.desc' },
    ],
  },
  showcaseHeadingKey: 'screensHeading',
  showcase: [
    { kind: 'video', src: 'demo.mp4', poster: 'home.png' },
    {
      kind: 'gallery',
      cols: 3,
      images: [
        { src: 'home.png', altKey: 'screens.home', labelKey: 'screens.home' },
        { src: 'browse.png', altKey: 'screens.browse', labelKey: 'screens.browse' },
        { src: 'tour-detail.png', altKey: 'screens.detail', labelKey: 'screens.detail' },
        { src: 'booking.png', altKey: 'screens.booking', labelKey: 'screens.booking' },
        { src: 'payment.png', altKey: 'screens.payment', labelKey: 'screens.payment' },
        { src: 'confirmation.png', altKey: 'screens.confirmation', labelKey: 'screens.confirmation' },
        { src: 'bundle.png', altKey: 'screens.bundle', labelKey: 'screens.bundle' },
        { src: 'broker.png', altKey: 'screens.broker', labelKey: 'screens.broker' },
      ],
    },
  ],
};

const spaconsole: ProjectConfig = {
  slug: 'spaconsole',
  ns: 'spaconsole',
  navKey: 'nav.portfolio_spaconsole',
  cardTitleKey: 'portfolioIndex.spaconsole.title',
  cardDescKey: 'portfolioIndex.spaconsole.desc',
  domainKey: 'portfolioIndex.spaconsole.domain',
  thumbnail: '/spaconsole/console.png',
  featured: true,
  order: 9,
  imagePrefix: '/spaconsole/',
  orbs: [
    'orb absolute top-40 left-1/4 w-96 h-96 bg-neon-green/10 rounded-full blur-3xl',
    'orb-delayed absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-neon-purple/10 rounded-full blur-3xl opacity-50',
  ],
  hero: {
    accent: 'green',
    pillAccent: 'purple',
    badgeGlow: true,
    badgeGlowGradient: 'from-neon-green/20 to-neon-purple/20',
    titleGradient: 'from-neon-green via-neon-cyan to-neon-purple',
    tech: ['Next.js', 'TypeScript', 'Prisma', 'Tailwind', 'shadcn/ui', 'LINE Messaging API', 'PromptPay'],
  },
  bento: {
    iconBorder: true,
    features: [
      { icon: CalendarCheck, size: 'wide', accent: 'green', titleKey: 'features.booking.title', descKey: 'features.booking.desc', orbPos: 'tr' },
      { icon: Sparkles, size: 'small', accent: 'purple', titleKey: 'features.portal.title', descKey: 'features.portal.desc' },
      { icon: Scissors, size: 'small', accent: 'cyan', titleKey: 'features.notes.title', descKey: 'features.notes.desc' },
      { icon: CreditCard, size: 'wide', accent: 'green', titleKey: 'features.pos.title', descKey: 'features.pos.desc', orbPos: 'bl', descBottomGap: true },
      { icon: BellRing, size: 'full', accent: 'purple', titleKey: 'features.reminders.title', descKey: 'features.reminders.desc' },
    ],
  },
  showcaseHeadingKey: 'screensHeading',
  showcase: [
    { kind: 'row', titleKey: 'concepts.console.title', descKey: 'concepts.console.desc', image: { src: 'console.png', altKey: 'concepts.console.alt' }, imgFit: 'cover', gradient: 'green-purple' },
    {
      kind: 'gallery',
      cols: 2,
      images: [
        { src: 'portal.png', altKey: 'screens.portal', labelKey: 'screens.portal' },
        { src: 'line.png', altKey: 'screens.line', labelKey: 'screens.line' },
      ],
    },
  ],
};

const consignmatch: ProjectConfig = {
  slug: 'consignmatch',
  ns: 'consignmatch',
  navKey: 'nav.portfolio_consignmatch',
  cardTitleKey: 'portfolioIndex.consignmatch.title',
  cardDescKey: 'portfolioIndex.consignmatch.desc',
  domainKey: 'portfolioIndex.consignmatch.domain',
  thumbnail: '/consignmatch/match-feed.png',
  featured: true,
  order: 10,
  imagePrefix: '/consignmatch/',
  orbs: [
    'orb absolute top-40 left-1/4 w-96 h-96 bg-neon-purple/10 rounded-full blur-3xl',
    'orb-delayed absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-neon-cyan/10 rounded-full blur-3xl opacity-50',
  ],
  hero: {
    accent: 'purple',
    pillAccent: 'cyan',
    badgeGlow: true,
    badgeGlowGradient: 'from-neon-purple/20 to-neon-cyan/20',
    titleGradient: 'from-neon-purple via-neon-cyan to-neon-green',
    tech: ['Next.js', 'TypeScript', 'Prisma', 'Tailwind', 'shadcn/ui', 'Auth.js', 'Cloudinary'],
  },
  bento: {
    iconBorder: true,
    features: [
      { icon: Search, size: 'wide', accent: 'purple', titleKey: 'features.match.title', descKey: 'features.match.desc', orbPos: 'tr' },
      { icon: History, size: 'small', accent: 'cyan', titleKey: 'features.memory.title', descKey: 'features.memory.desc' },
      { icon: Star, size: 'small', accent: 'green', titleKey: 'features.trust.title', descKey: 'features.trust.desc' },
      { icon: Camera, size: 'wide', accent: 'cyan', titleKey: 'features.listing.title', descKey: 'features.listing.desc', orbPos: 'bl', descBottomGap: true },
      { icon: Tag, size: 'full', accent: 'purple', titleKey: 'features.prefs.title', descKey: 'features.prefs.desc' },
    ],
  },
  showcaseHeadingKey: 'screensHeading',
  showcase: [
    {
      kind: 'gallery',
      cols: 3,
      images: [
        { src: 'match-feed.png', altKey: 'screens.feed', labelKey: 'screens.feed' },
        { src: 'item-detail.png', altKey: 'screens.detail', labelKey: 'screens.detail' },
        { src: 'create-listing.png', altKey: 'screens.create', labelKey: 'screens.create' },
        { src: 'my-listings.png', altKey: 'screens.mine', labelKey: 'screens.mine' },
        { src: 'preferences.png', altKey: 'screens.prefs', labelKey: 'screens.prefs' },
        { src: 'profile.png', altKey: 'screens.profile', labelKey: 'screens.profile' },
      ],
    },
  ],
};

const tutorschool: ProjectConfig = {
  slug: 'tutorschool',
  ns: 'tutorschool',
  navKey: 'nav.portfolio_tutorschool',
  cardTitleKey: 'portfolioIndex.tutorschool.title',
  cardDescKey: 'portfolioIndex.tutorschool.desc',
  domainKey: 'portfolioIndex.tutorschool.domain',
  thumbnail: '/tutorschool/dashboard.png',
  order: 11,
  imagePrefix: '/tutorschool/',
  orbs: [
    'orb absolute top-40 left-1/4 w-96 h-96 bg-neon-blue/10 rounded-full blur-3xl',
    'orb-delayed absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-neon-cyan/10 rounded-full blur-3xl opacity-50',
  ],
  hero: {
    accent: 'blue',
    pillAccent: 'blue',
    badgeGlow: true,
    badgeGlowGradient: 'from-neon-blue/20 to-neon-cyan/20',
    titleGradient: 'from-neon-blue via-neon-cyan to-neon-purple',
    tech: ['Next.js', 'TypeScript', 'Prisma', 'Tailwind', 'shadcn/ui', 'IBM Plex Sans Thai'],
  },
  bento: {
    iconBorder: true,
    features: [
      { icon: ClipboardCheck, size: 'wide', accent: 'blue', titleKey: 'features.checkin.title', descKey: 'features.checkin.desc', orbPos: 'tr' },
      { icon: History, size: 'small', accent: 'cyan', titleKey: 'features.ledger.title', descKey: 'features.ledger.desc' },
      { icon: Wallet, size: 'small', accent: 'purple', titleKey: 'features.wallets.title', descKey: 'features.wallets.desc' },
      { icon: Users, size: 'wide', accent: 'cyan', titleKey: 'features.pay.title', descKey: 'features.pay.desc', orbPos: 'bl', descBottomGap: true },
      { icon: CalendarDays, size: 'full', accent: 'blue', titleKey: 'features.calendar.title', descKey: 'features.calendar.desc' },
    ],
  },
  showcaseHeadingKey: 'conceptsHeading',
  showcase: [
    { kind: 'row', titleKey: 'concepts.dashboard.title', descKey: 'concepts.dashboard.desc', image: { src: 'dashboard.png', altKey: 'concepts.dashboard.alt' }, imgFit: 'cover', gradient: 'blue-cyan' },
    { kind: 'row', reverse: true, titleKey: 'concepts.checkin.title', descKey: 'concepts.checkin.desc', image: { src: 'checkin.png', altKey: 'concepts.checkin.alt' }, imgFit: 'cover', gradient: 'cyan-purple' },
    { kind: 'row', titleKey: 'concepts.schedule.title', descKey: 'concepts.schedule.desc', image: { src: 'schedule.png', altKey: 'concepts.schedule.alt' }, imgFit: 'cover', gradient: 'blue-cyan' },
    { kind: 'row', reverse: true, titleKey: 'concepts.students.title', descKey: 'concepts.students.desc', image: { src: 'students.png', altKey: 'concepts.students.alt' }, imgFit: 'cover', gradient: 'cyan-purple' },
    { kind: 'row', titleKey: 'concepts.pay.title', descKey: 'concepts.pay.desc', image: { src: 'teacher-pay.png', altKey: 'concepts.pay.alt' }, imgFit: 'cover', gradient: 'blue-green' },
  ],
};

const stocktake: ProjectConfig = {
  slug: 'stocktake',
  ns: 'stocktake',
  navKey: 'nav.portfolio_stocktake',
  cardTitleKey: 'portfolioIndex.stocktake.title',
  cardDescKey: 'portfolioIndex.stocktake.desc',
  domainKey: 'portfolioIndex.stocktake.domain',
  thumbnail: '/stocktake/dashboard.png',
  order: 12,
  imagePrefix: '/stocktake/',
  orbs: [
    'orb absolute top-40 left-1/4 w-96 h-96 bg-neon-cyan/10 rounded-full blur-3xl',
    'orb-delayed absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-neon-green/10 rounded-full blur-3xl opacity-50',
  ],
  hero: {
    accent: 'cyan',
    pillAccent: 'cyan',
    badgeGlow: true,
    badgeGlowGradient: 'from-neon-cyan/20 to-neon-green/20',
    titleGradient: 'from-neon-cyan via-neon-green to-neon-blue',
    tech: ['Web App', 'Tailwind CSS', 'shadcn/ui', 'IBM Plex Sans Thai', 'Lucide Icons'],
  },
  bento: {
    iconBorder: true,
    features: [
      { icon: Gauge, size: 'wide', accent: 'cyan', titleKey: 'features.balance.title', descKey: 'features.balance.desc', orbPos: 'tr' },
      { icon: ArrowDownToLine, size: 'small', accent: 'green', titleKey: 'features.fast.title', descKey: 'features.fast.desc' },
      { icon: BellRing, size: 'small', accent: 'purple', titleKey: 'features.alerts.title', descKey: 'features.alerts.desc' },
      { icon: Wallet, size: 'wide', accent: 'green', titleKey: 'features.value.title', descKey: 'features.value.desc', orbPos: 'bl', descBottomGap: true },
      { icon: History, size: 'full', accent: 'cyan', titleKey: 'features.audit.title', descKey: 'features.audit.desc' },
    ],
  },
  showcaseHeadingKey: 'conceptsHeading',
  showcase: [
    { kind: 'row', titleKey: 'concepts.dashboard.title', descKey: 'concepts.dashboard.desc', image: { src: 'dashboard.png', altKey: 'concepts.dashboard.alt' }, imgFit: 'cover', gradient: 'cyan-green' },
    { kind: 'row', reverse: true, titleKey: 'concepts.products.title', descKey: 'concepts.products.desc', image: { src: 'products.png', altKey: 'concepts.products.alt' }, imgFit: 'cover', gradient: 'green-purple' },
    { kind: 'row', titleKey: 'concepts.inout.title', descKey: 'concepts.inout.desc', image: { src: 'stock-out.png', altKey: 'concepts.inout.alt' }, imgFit: 'cover', gradient: 'cyan-blue' },
    { kind: 'row', reverse: true, titleKey: 'concepts.mobile.title', descKey: 'concepts.mobile.desc', image: { src: 'mobile.png', altKey: 'concepts.mobile.alt' }, imgFit: 'cover', gradient: 'green-cyan' },
  ],
};

const clinicqueue: ProjectConfig = {
  slug: 'clinicqueue',
  ns: 'clinicqueue',
  navKey: 'nav.portfolio_clinicqueue',
  cardTitleKey: 'portfolioIndex.clinicqueue.title',
  cardDescKey: 'portfolioIndex.clinicqueue.desc',
  domainKey: 'portfolioIndex.clinicqueue.domain',
  thumbnail: '/clinicqueue/staff-queue.png',
  order: 13,
  imagePrefix: '/clinicqueue/',
  orbs: [
    'orb absolute top-40 left-1/4 w-96 h-96 bg-neon-cyan/10 rounded-full blur-3xl',
    'orb-delayed absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-neon-green/10 rounded-full blur-3xl opacity-50',
  ],
  hero: {
    accent: 'cyan',
    pillAccent: 'green',
    badgeGlow: true,
    badgeGlowGradient: 'from-neon-cyan/20 to-neon-green/20',
    titleGradient: 'from-neon-cyan via-neon-green to-neon-blue',
    tech: ['Next.js', 'TypeScript', 'PostgreSQL + RLS', 'Auth.js', 'shadcn/ui', 'LINE LIFF'],
  },
  bento: {
    iconBorder: true,
    features: [
      { icon: Languages, size: 'wide', accent: 'cyan', titleKey: 'features.channels.title', descKey: 'features.channels.desc', orbPos: 'tr' },
      { icon: Lock, size: 'small', accent: 'green', titleKey: 'features.pdpa.title', descKey: 'features.pdpa.desc' },
      { icon: CalendarCheck, size: 'small', accent: 'purple', titleKey: 'features.queue.title', descKey: 'features.queue.desc' },
      { icon: Stethoscope, size: 'wide', accent: 'green', titleKey: 'features.board.title', descKey: 'features.board.desc', orbPos: 'bl', descBottomGap: true },
      { icon: Droplets, size: 'full', accent: 'cyan', titleKey: 'features.results.title', descKey: 'features.results.desc' },
    ],
  },
  showcaseHeadingKey: 'screensHeading',
  showcase: [
    {
      kind: 'gallery',
      cols: 3,
      images: [
        { src: 'home.png', altKey: 'screens.home', labelKey: 'screens.home' },
        { src: 'queue.png', altKey: 'screens.queue', labelKey: 'screens.queue' },
        { src: 'results.png', altKey: 'screens.results', labelKey: 'screens.results' },
        { src: 'result-detail.png', altKey: 'screens.resultDetail', labelKey: 'screens.resultDetail' },
        { src: 'link-account.png', altKey: 'screens.link', labelKey: 'screens.link' },
      ],
    },
    {
      kind: 'pair',
      columns: [
        { titleKey: 'concepts.staffQueue.title', descKey: 'concepts.staffQueue.desc', images: [{ src: 'staff-queue.png', altKey: 'concepts.staffQueue.alt' }], imgFit: 'contain', gradient: 'cyan-blue' },
        { titleKey: 'concepts.staffResult.title', descKey: 'concepts.staffResult.desc', images: [{ src: 'staff-result.png', altKey: 'concepts.staffResult.alt' }], imgFit: 'contain', gradient: 'green-cyan' },
      ],
    },
  ],
};

const facecheck: ProjectConfig = {
  slug: 'facecheck',
  ns: 'facecheck',
  navKey: 'nav.portfolio_facecheck',
  cardTitleKey: 'portfolioIndex.facecheck.title',
  cardDescKey: 'portfolioIndex.facecheck.desc',
  domainKey: 'portfolioIndex.facecheck.domain',
  thumbnail: '/facecheck/dash-today.png',
  order: 14,
  imagePrefix: '/facecheck/',
  orbs: [
    'orb absolute top-40 right-1/4 w-96 h-96 bg-neon-blue/10 rounded-full blur-3xl',
    'orb-delayed absolute top-1/2 left-1/4 w-[500px] h-[500px] bg-neon-green/10 rounded-full blur-3xl opacity-50',
  ],
  hero: {
    accent: 'blue',
    pillAccent: 'green',
    badgeGlow: true,
    badgeGlowGradient: 'from-neon-blue/20 to-neon-green/20',
    titleGradient: 'from-neon-blue via-neon-green to-neon-cyan',
    tech: ['Raspberry Pi 5', 'Python + FastAPI', 'facenet-pytorch', 'SQLite (WAL)', 'HTMX + Jinja2', 'PDPA by design'],
  },
  bento: {
    iconBorder: true,
    features: [
      { icon: ScanFace, size: 'wide', accent: 'green', titleKey: 'features.recognize.title', descKey: 'features.recognize.desc', orbPos: 'tr' },
      { icon: Cpu, size: 'small', accent: 'blue', titleKey: 'features.onDevice.title', descKey: 'features.onDevice.desc' },
      { icon: WifiOff, size: 'small', accent: 'cyan', titleKey: 'features.offline.title', descKey: 'features.offline.desc' },
      { icon: Lock, size: 'wide', accent: 'purple', titleKey: 'features.consent.title', descKey: 'features.consent.desc', orbPos: 'bl', descBottomGap: true },
      { icon: Clock, size: 'full', accent: 'green', titleKey: 'features.hours.title', descKey: 'features.hours.desc' },
    ],
  },
  showcaseHeadingKey: 'screensHeading',
  showcase: [
    {
      kind: 'gallery',
      cols: 2,
      gradient: 'blue-green',
      images: [
        { src: 'kiosk-idle.png', altKey: 'screens.idle', labelKey: 'screens.idle' },
        { src: 'kiosk-scanning.png', altKey: 'screens.scanning', labelKey: 'screens.scanning' },
        { src: 'kiosk-checkin.png', altKey: 'screens.checkin', labelKey: 'screens.checkin' },
        { src: 'kiosk-checkout.png', altKey: 'screens.checkout', labelKey: 'screens.checkout' },
      ],
    },
    {
      kind: 'row',
      titleKey: 'concepts.consent.title',
      descKey: 'concepts.consent.desc',
      image: { src: 'kiosk-consent.png', altKey: 'concepts.consent.alt' },
      imgFit: 'contain',
      gradient: 'purple-cyan',
    },
    {
      kind: 'row',
      reverse: true,
      titleKey: 'concepts.today.title',
      descKey: 'concepts.today.desc',
      image: { src: 'dash-today.png', altKey: 'concepts.today.alt' },
      imgFit: 'contain',
      gradient: 'blue-cyan',
    },
    {
      kind: 'pair',
      columns: [
        { titleKey: 'concepts.roster.title', descKey: 'concepts.roster.desc', images: [{ src: 'dash-roster.png', altKey: 'concepts.roster.alt' }], imgFit: 'contain', gradient: 'cyan-blue' },
        { titleKey: 'concepts.hours.title', descKey: 'concepts.hours.desc', images: [{ src: 'dash-hours.png', altKey: 'concepts.hours.alt' }], imgFit: 'contain', gradient: 'green-cyan' },
      ],
    },
    {
      kind: 'pair',
      columns: [
        { titleKey: 'concepts.erasure.title', descKey: 'concepts.erasure.desc', images: [{ src: 'dash-person.png', altKey: 'concepts.erasure.alt' }], imgFit: 'contain', gradient: 'purple-cyan' },
        { titleKey: 'concepts.backup.title', descKey: 'concepts.backup.desc', images: [{ src: 'dash-backup.png', altKey: 'concepts.backup.alt' }], imgFit: 'contain', gradient: 'blue-green' },
      ],
    },
  ],
};

const classcredit: ProjectConfig = {
  slug: 'classcredit',
  ns: 'classcredit',
  navKey: 'nav.portfolio_classcredit',
  cardTitleKey: 'portfolioIndex.classcredit.title',
  cardDescKey: 'portfolioIndex.classcredit.desc',
  domainKey: 'portfolioIndex.classcredit.domain',
  thumbnail: '/classcredit/staff-dashboard.png',
  order: 15,
  imagePrefix: '/classcredit/',
  orbs: [
    'orb absolute top-40 left-1/4 w-96 h-96 bg-neon-blue/10 rounded-full blur-3xl',
    'orb-delayed absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-neon-green/10 rounded-full blur-3xl opacity-50',
  ],
  hero: {
    accent: 'blue',
    pillAccent: 'green',
    badgeGlow: true,
    badgeGlowGradient: 'from-neon-blue/20 to-neon-green/20',
    titleGradient: 'from-neon-blue via-neon-cyan to-neon-green',
    tech: ['Next.js', 'TypeScript', 'PostgreSQL', 'LINE LIFF', 'Drizzle', 'Tailwind'],
  },
  bento: {
    iconBorder: true,
    features: [
      { icon: ScrollText, size: 'wide', accent: 'blue', titleKey: 'features.ledger.title', descKey: 'features.ledger.desc', orbPos: 'tr' },
      { icon: Clock, size: 'small', accent: 'green', titleKey: 'features.cutoff.title', descKey: 'features.cutoff.desc' },
      { icon: MessageCircle, size: 'small', accent: 'cyan', titleKey: 'features.line.title', descKey: 'features.line.desc' },
      { icon: ShieldCheck, size: 'wide', accent: 'purple', titleKey: 'features.guard.title', descKey: 'features.guard.desc', orbPos: 'bl', descBottomGap: true },
      { icon: Users, size: 'full', accent: 'blue', titleKey: 'features.guardian.title', descKey: 'features.guardian.desc' },
    ],
  },
  showcaseHeadingKey: 'screensHeading',
  showcase: [
    {
      kind: 'row',
      titleKey: 'concepts.dashboard.title',
      descKey: 'concepts.dashboard.desc',
      image: { src: 'staff-dashboard.png', altKey: 'concepts.dashboard.alt' },
      imgFit: 'contain',
      gradient: 'blue-cyan',
    },
    {
      kind: 'row',
      reverse: true,
      titleKey: 'concepts.booking.title',
      descKey: 'concepts.booking.desc',
      image: { src: 'staff-booking.png', altKey: 'concepts.booking.alt' },
      imgFit: 'contain',
      gradient: 'cyan-purple',
    },
    {
      kind: 'pair',
      columns: [
        { titleKey: 'concepts.availability.title', descKey: 'concepts.availability.desc', images: [{ src: 'staff-availability.png', altKey: 'concepts.availability.alt' }], imgFit: 'contain', gradient: 'blue-green' },
        { titleKey: 'concepts.schedule.title', descKey: 'concepts.schedule.desc', images: [{ src: 'staff-schedule.png', altKey: 'concepts.schedule.alt' }], imgFit: 'contain', gradient: 'cyan-blue' },
      ],
    },
    {
      kind: 'pair',
      columns: [
        { titleKey: 'concepts.requests.title', descKey: 'concepts.requests.desc', images: [{ src: 'staff-requests.png', altKey: 'concepts.requests.alt' }], imgFit: 'contain', gradient: 'green-cyan' },
        { titleKey: 'concepts.students.title', descKey: 'concepts.students.desc', images: [{ src: 'staff-students.png', altKey: 'concepts.students.alt' }], imgFit: 'contain', gradient: 'purple-cyan' },
      ],
    },
    {
      kind: 'gallery',
      cols: 3,
      gradient: 'blue-green',
      images: [
        { src: 'liff-home.png', altKey: 'screens.home', labelKey: 'screens.home' },
        { src: 'liff-credit.png', altKey: 'screens.credit', labelKey: 'screens.credit' },
        { src: 'liff-history.png', altKey: 'screens.history', labelKey: 'screens.history' },
      ],
    },
    {
      kind: 'pair',
      columns: [
        { titleKey: 'concepts.refund.title', descKey: 'concepts.refund.desc', images: [{ src: 'liff-req-refund.png', altKey: 'concepts.refund.alt' }], imgFit: 'contain', gradient: 'green-cyan' },
        { titleKey: 'concepts.norefund.title', descKey: 'concepts.norefund.desc', images: [{ src: 'liff-req-norefund.png', altKey: 'concepts.norefund.alt' }], imgFit: 'contain', gradient: 'orange-cyan' },
      ],
    },
  ],
};

const boutiquehotel: ProjectConfig = {
  slug: 'boutiquehotel',
  ns: 'boutiquehotel',
  navKey: 'nav.portfolio_boutiquehotel',
  cardTitleKey: 'portfolioIndex.boutiquehotel.title',
  cardDescKey: 'portfolioIndex.boutiquehotel.desc',
  domainKey: 'portfolioIndex.boutiquehotel.domain',
  thumbnail: '/boutiquehotel/home.png',
  order: 16,
  imagePrefix: '/boutiquehotel/',
  orbs: [
    'orb absolute top-40 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl',
    'orb-delayed absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-neon-cyan/10 rounded-full blur-3xl opacity-50',
  ],
  hero: {
    accent: 'orange',
    pillAccent: 'cyan',
    badgeGlow: true,
    badgeGlowGradient: 'from-orange-500/20 to-neon-cyan/20',
    titleGradient: 'from-orange-500 via-neon-cyan to-neon-blue',
    tech: ['Laravel', 'Filament', 'Livewire', 'MySQL', 'Tailwind', 'PromptPay'],
  },
  bento: {
    iconBorder: true,
    features: [
      { icon: CalendarCheck, size: 'wide', accent: 'orange', titleKey: 'features.realtime.title', descKey: 'features.realtime.desc', orbPos: 'tr' },
      { icon: QrCode, size: 'small', accent: 'cyan', titleKey: 'features.payment.title', descKey: 'features.payment.desc' },
      { icon: Languages, size: 'small', accent: 'blue', titleKey: 'features.bilingual.title', descKey: 'features.bilingual.desc' },
      { icon: CalendarRange, size: 'wide', accent: 'cyan', titleKey: 'features.ota.title', descKey: 'features.ota.desc', orbPos: 'bl', descBottomGap: true },
      { icon: ShieldCheck, size: 'full', accent: 'orange', titleKey: 'features.pdpa.title', descKey: 'features.pdpa.desc' },
    ],
  },
  showcaseHeadingKey: 'screensHeading',
  showcase: [
    {
      kind: 'row',
      titleKey: 'concepts.home.title',
      descKey: 'concepts.home.desc',
      image: { src: 'home.png', altKey: 'concepts.home.alt' },
      imgFit: 'contain',
      gradient: 'orange-cyan',
    },
    {
      kind: 'row',
      reverse: true,
      titleKey: 'concepts.search.title',
      descKey: 'concepts.search.desc',
      image: { src: 'booking-dates.png', altKey: 'concepts.search.alt' },
      imgFit: 'contain',
      gradient: 'cyan-blue',
    },
    {
      kind: 'pair',
      columns: [
        { titleKey: 'concepts.guest.title', descKey: 'concepts.guest.desc', images: [{ src: 'booking-guest.png', altKey: 'concepts.guest.alt' }], imgFit: 'contain', gradient: 'blue-cyan' },
        { titleKey: 'concepts.payment.title', descKey: 'concepts.payment.desc', images: [{ src: 'booking-payment.png', altKey: 'concepts.payment.alt' }], imgFit: 'contain', gradient: 'orange-cyan' },
      ],
    },
    {
      kind: 'pair',
      columns: [
        { titleKey: 'concepts.confirm.title', descKey: 'concepts.confirm.desc', images: [{ src: 'booking-confirm.png', altKey: 'concepts.confirm.alt' }], imgFit: 'contain', gradient: 'green-cyan' },
        { titleKey: 'concepts.rooms.title', descKey: 'concepts.rooms.desc', images: [{ src: 'rooms.png', altKey: 'concepts.rooms.alt' }], imgFit: 'contain', gradient: 'cyan-purple' },
      ],
    },
    {
      kind: 'row',
      titleKey: 'concepts.calendar.title',
      descKey: 'concepts.calendar.desc',
      image: { src: 'admin-calendar.png', altKey: 'concepts.calendar.alt' },
      imgFit: 'contain',
      gradient: 'orange-cyan',
    },
    {
      kind: 'row',
      reverse: true,
      titleKey: 'concepts.bookings.title',
      descKey: 'concepts.bookings.desc',
      image: { src: 'admin-bookings.png', altKey: 'concepts.bookings.alt' },
      imgFit: 'contain',
      gradient: 'blue-green',
    },
    {
      kind: 'pair',
      columns: [
        { titleKey: 'concepts.rates.title', descKey: 'concepts.rates.desc', images: [{ src: 'admin-rates.png', altKey: 'concepts.rates.alt' }], imgFit: 'contain', gradient: 'cyan-blue' },
        { titleKey: 'concepts.cms.title', descKey: 'concepts.cms.desc', images: [{ src: 'admin-cms.png', altKey: 'concepts.cms.alt' }], imgFit: 'contain', gradient: 'green-purple' },
      ],
    },
  ],
};

export const PROJECTS: ProjectConfig[] = [inventory, tradeplanner, pos, petclinic, bangkoktable4, qaranger, csenergy, tourandaman, spaconsole, consignmatch, tutorschool, stocktake, clinicqueue, facecheck, classcredit, boutiquehotel];

const BY_SLUG: Record<string, ProjectConfig> = Object.fromEntries(PROJECTS.map((p) => [p.slug, p]));

export function getProject(slug: string): ProjectConfig | undefined {
  return BY_SLUG[slug];
}

export function listProjects(): ProjectConfig[] {
  return [...PROJECTS].sort((a, b) => a.order - b.order);
}
